import acc from "accounting"
import { Flex, Typography } from "antd"

import { titleSortFilters } from "../../../../Components/titleSortFilters"
import Icon from "../../../../Icon"
import IconCell from "../IconCell/IconCell"
import RowActionMenu from "../RowActionMenu/RowActionMenu"
import UiNameCell from "../UiNameCell/UiNameCell"

import styles from "./columns.module.less"

const { Text } = Typography

const formatCount = (value) => acc.formatNumber(value || 0, 0, ",", ".")

/**
 * The six columns of the Payment Matching table, in fixed order.
 *
 * Current name, UI name, Payment processor and Number of transactions are sortable; Icon and Action
 * are not. Only two cells are editable — the UI name and the icon — and both delegate to the page
 * through `editing`, because a row cannot decide on its own whether another row is already open.
 */
// Which direction, if any, a column is currently sorted in. Derived from the page's own ordering
// string, because the table is told what to show rather than keeping its own sorter state — without
// this the default sort by volume would render a neutral indicator on load.
const sortOrderFor = (key, ordering) => {
  if (ordering === key) return "ascend"
  if (ordering === `-${key}`) return "descend"

  return null
}

export const getColumns = ({ t, editing, ordering }) => [
  {
    key: "current_name",
    dataIndex: "current_name",
    title: (state) =>
      titleSortFilters({
        title: t("Current name"),
        sorter: true,
        stateData: state,
        key: "current_name"
      }),
    sorter: true,
    sortOrder: sortOrderFor("current_name", ordering),
    // The raw value exactly as received, including casing and underscores.
    render: (currentName) => <Text className={styles.rawValue}>{currentName}</Text>
  },
  {
    key: "ui_name",
    dataIndex: "ui_name",
    title: (state) =>
      titleSortFilters({
        title: t("UI name"),
        sorter: true,
        stateData: state,
        key: "ui_name"
      }),
    sorter: true,
    sortOrder: sortOrderFor("ui_name", ordering),
    render: (uiName, record) => (
      <UiNameCell
        record={record}
        isEditing={editing.editingId === record.id}
        isChecking={editing.groupPending === record.id}
        onStartEdit={editing.onStartEdit}
        onCancel={editing.onCancelEdit}
        onSave={editing.onSaveName}
      />
    )
  },
  {
    key: "payment_processor",
    dataIndex: "payment_processor",
    title: (state) =>
      titleSortFilters({
        title: t("Payment processor"),
        sorter: true,
        stateData: state,
        key: "payment_processor"
      }),
    sorter: true,
    sortOrder: sortOrderFor("payment_processor", ordering),
    render: (processor) => <Text className={styles.processor}>{processor}</Text>
  },
  {
    key: "transactions_count",
    dataIndex: "transactions_count",
    align: "right",
    title: (state) =>
      titleSortFilters({
        title: t("Number of transactions"),
        sorter: true,
        stateData: state,
        key: "transactions_count",
        justify: "end"
      }),
    sorter: true,
    sortOrder: sortOrderFor("transactions_count", ordering),
    // Tabular figures and a thousands separator, so magnitudes are comparable down the column.
    render: (count) => (
      <Text className={count ? styles.count : styles.zeroCount}>{formatCount(count)}</Text>
    )
  },
  {
    key: "icon",
    dataIndex: "icon_url",
    title: () => (
      <Flex align="center" gap={6}>
        <Icon type="pictures" className={styles.headerIcon} />
        {t("Icon")}
      </Flex>
    ),
    render: (iconUrl, record) => <IconCell iconUrl={iconUrl} rawValue={record.current_name} />
  },
  {
    key: "action",
    title: () => t("Action"),
    render: (_, record) => (
      <RowActionMenu
        record={record}
        onOpen={editing.onOpenRowMenu}
        onViewHistory={editing.onViewHistory}
        onAddIcon={editing.onAddIcon}
        onDeleteIcon={editing.onDeleteIcon}
      />
    )
  }
]
